import React from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Chip, Avatar } from '@mui/material';
import { School, CalendarToday, LocationOn, Grade } from '@mui/icons-material';

const Education = () => {
  const educationData = [
    {
      id: 1,
      degree: 'Master of Science in Computer Science',
      institution: 'University of Maryland, Baltimore County',
      location: 'Baltimore, Maryland, USA',
      duration: '2022 - 2023',
      gpa: '3.8/4.0',
      description: 'Specialized in software engineering and artificial intelligence. Completed coursework in advanced algorithms, machine learning, and distributed systems.',
      courses: ['Advanced Algorithms', 'Machine Learning', 'Distributed Systems', 'Software Engineering', 'Database Systems'],
      logo: require('../Assests/umbc.png'),
      featured: false,
    },
    {
      id: 2,
      degree: 'Bachelor of Technology in Computer Science',
      institution: 'Vignan Foundation for Science Technology and Research',
      location: 'Hyderabad, India',
      duration: '2015 - 2019',
      gpa: '3.8/4.0',
      description: 'Focused on computer science fundamentals with emphasis on programming, data structures, and software development methodologies.',
      courses: ['Data Structures', 'Object-Oriented Programming', 'Computer Networks', 'Operating Systems', 'Web Technologies'],
      logo: require('../Assests/vignan.jpg'),
      featured: false,
    },
  ];

  const certifications = [
    {
      name: 'Data Warehousing for Business Intelligence',
      issuer: 'Coursera',
      year: '2019',
      credential: 'PAKW3K8G4U2Z',
    },
    {
      name: 'Relational Database Support for Data Warehouses',
      issuer: 'Coursera',
      year: '2019',
      credential: 'MSXEZ9XE2D46',
    },
    {
      name: 'Exam 778: Analyzing and Visualizing Data with Power BI',
      issuer: 'Microsoft',
      year: '2019',
      credential: 'df060eeb-5407-4405-a2a0-9950c299e27e',
    },
    {
      name: 'Exam 761: Querying Data with Transact-SQL',
      issuer: 'Microsoft',
      year: '2019',
      credential: 'df060eeb-5407-4405-a2a0-9950c299e27e',
    }

  ];

  return (
    <Box
      component="section"
      id="education"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'var(--bg-primary)',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'var(--font-display)',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                color: 'var(--text-primary)',
              }}
            >
              Education & Certifications
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.125rem',
                color: 'var(--text-secondary)',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              My academic journey and professional certifications that have shaped 
              my expertise in software development and technology.
            </Typography>
          </Box>
        </motion.div>

        {/* Education Cards */}
        <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }} alignItems="stretch">
          {educationData.map((education, index) => (
            <Grid item xs={12} lg={6} key={education.id} style={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ flex: 1, display: 'flex' }}
              >
                <Paper
                  elevation={education.featured ? 8 : 2}
                  sx={{
                    p: 4,
                    height: 'auto',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--border)',
                    background: education.featured 
                      ? 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)'
                      : 'var(--bg-primary)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'var(--transition)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 'var(--shadow-xl)',
                    },
                    ...(education.featured && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%)',
                      },
                    }),
                  }}
                >
                  {/* Institution Logo */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={education.logo}
                      alt={education.institution}
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        border: '2px solid var(--border)',
                      }}
                    >
                      <School />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          mb: 0.5,
                        }}
                      >
                        {education.institution}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--primary)',
                          fontWeight: 600,
                        }}
                      >
                        {education.degree}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Duration and Location */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'var(--text-muted)', fontWeight: 500 }}
                      >
                        {education.duration}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'var(--text-muted)', fontWeight: 500 }}
                      >
                        {education.location}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Grade sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'var(--text-muted)', fontWeight: 500 }}
                      >
                        GPA: {education.gpa}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'var(--text-secondary)',
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    {education.description}
                  </Typography>

                  {/* Courses */}
                  {/* <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        mb: 2,
                      }}
                    >
                      Key Courses:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {education.courses.map((course) => (
                        <Chip
                          key={course}
                          label={course}
                          size="small"
                          sx={{
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border)',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Box>
                  </Box> */}

                  {/* Featured Badge */}
                  {education.featured && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: 'var(--primary)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Current
                    </Box>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Certifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                mb: 2,
                color: 'var(--text-primary)',
              }}
            >
              Professional Certifications
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'var(--text-secondary)',
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              Industry-recognized certifications that validate my expertise in cloud platforms and development technologies.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {certifications.map((cert, index) => (
              <Grid item xs={12} sm={6} md={4} key={cert.name} style={{ display: 'flex' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ flex: 1, display: 'flex' }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: 180,
                      minHeight: 180,
                      maxHeight: 180,
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--border)',
                      transition: 'var(--transition)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 'var(--shadow-lg)',
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        mb: 1,
                      }}
                    >
                      {cert.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--primary)',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {cert.issuer}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--text-secondary)',
                        mb: 2,
                      }}
                    >
                      {cert.year} • {cert.credential}
                    </Typography>
                    <Chip
                      label="Verified"
                      size="small"
                      sx={{
                        backgroundColor: 'var(--accent)',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Education; 